import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AlertCircle, Landmark, Loader2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  formatIban,
  type BalanceAdjustment,
  type Payout,
  type PayoutBalance,
  type PayoutBankAccount,
  type PayoutEventEarning,
  type PayoutStatus,
  type PayoutSummary,
} from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { QueryError } from "@/components/query-error";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  useCancelPayoutMutation,
  usePayoutSummaryQuery,
  useRequestPayoutMutation,
  useUpdateBankAccountMutation,
} from "@/features/payouts/api";
import { ApiError, showApiError } from "@/lib/api-client";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const FINANCE_DATE: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: ISTANBUL_TZ,
};

/**
 * Finans — organizatörün TicketNjoy'dan alacağı. Bakiye siparişlerden hesaplanır;
 * etkinlik bitince çekilebilir olur. Owner-only (IBAN + para).
 */
export function FinancePage() {
  return (
    <DashboardPage>
      <FinanceView />
    </DashboardPage>
  );
}

function FinanceView() {
  const query = usePayoutSummaryQuery();
  const t = useT("finance");

  return (
    <div className="space-y-6">
      <PageHeader title={t("title")} description={t("description")} />

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : query.error || !query.data ? (
        <QueryError
          message={t("loadError")}
          detail={query.error?.detail}
          onRetry={() => query.refetch()}
          retrying={query.isFetching}
        />
      ) : (
        <FinanceContent data={query.data} />
      )}
    </div>
  );
}

function FinanceContent({ data }: { data: PayoutSummary }) {
  const t = useT("finance");
  const [requestFor, setRequestFor] = useState<PayoutBalance | null>(null);
  const [bankOpen, setBankOpen] = useState(false);
  const hasBank = !!data.bankAccount.iban;

  return (
    <>
      {data.balances.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title={t("emptyTitle")}
          description={t("emptyDescription")}
        />
      ) : (
        data.balances.map((b) => (
          <BalanceCards
            key={b.currency}
            balance={b}
            showCurrency={data.balances.length > 1}
            hasOpenPayout={data.payouts.some(
              (p) => p.currency === b.currency && (p.status === "Requested" || p.status === "Approved"),
            )}
            hasBank={hasBank}
            onRequest={() => setRequestFor(b)}
          />
        ))
      )}

      <BankAccountCard bank={data.bankAccount} onEdit={() => setBankOpen(true)} />
      <PayoutsCard payouts={data.payouts} />
      {data.adjustments.length > 0 ? <AdjustmentsCard adjustments={data.adjustments} /> : null}
      {data.events.length > 0 ? <EventsCard events={data.events} /> : null}

      <Dialog open={bankOpen} onOpenChange={setBankOpen} disableBackdropClose>
        <DialogContent>
          <DialogHeader
            title={t("bank.title")}
            description={t("bank.dialogDescription")}
          />
          <BankAccountForm bank={data.bankAccount} onDone={() => setBankOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!requestFor} onOpenChange={(o) => !o && setRequestFor(null)} disableBackdropClose>
        <DialogContent>
          <DialogHeader
            title={t("request.dialogTitle")}
            description={t("request.dialogDescription")}
          />
          {requestFor ? (
            <RequestPayoutForm
              balance={requestFor}
              bank={data.bankAccount}
              onDone={() => setRequestFor(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─────────────────────────── Bakiye ─────────────────────────── */

function BalanceCards({
  balance: b,
  showCurrency,
  hasOpenPayout,
  hasBank,
  onRequest,
}: {
  balance: PayoutBalance;
  showCurrency: boolean;
  hasOpenPayout: boolean;
  hasBank: boolean;
  onRequest: () => void;
}) {
  const t = useT("finance.balance");
  const fmt = useFormat();
  const money = (v: number) => fmt.money(v, b.currency);
  const blockedReason = !hasBank
    ? t("needBank")
    : hasOpenPayout
      ? t("openPayout")
      : null;

  return (
    <section className="space-y-2">
      {showCurrency ? <h2 className="text-sm font-medium text-muted-foreground">{b.currency}</h2> : null}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{t("available")}</CardDescription>
            <CardTitle className={b.available < 0 ? "text-2xl text-destructive" : "text-2xl"}>
              {money(b.available)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground">
            <div className="space-y-0.5">
              {b.inProgress > 0 ? <div>{t("inProgress", { value: money(b.inProgress) })}</div> : null}
              {b.adjustments !== 0 ? (
                <div>
                  {t("adjustments", {
                    value: `${b.adjustments > 0 ? "+" : ""}${money(b.adjustments)}`,
                  })}
                </div>
              ) : null}
              {b.available < 0 ? <div>{t("negative")}</div> : null}
            </div>
            <Button
              size="sm"
              className="w-full"
              disabled={b.available <= 0 || !!blockedReason}
              onClick={onRequest}
            >
              {t("request")}
            </Button>
            {blockedReason ? <div>{blockedReason}</div> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{t("pending")}</CardDescription>
            <CardTitle className="text-2xl">{money(b.pendingEarnings)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">{t("pendingHint")}</CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{t("paidOut")}</CardDescription>
            <CardTitle className="text-2xl">{money(b.paidOut)}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">{t("paidOutHint")}</CardContent>
        </Card>
      </div>
    </section>
  );
}

/* ─────────────────────────── Banka hesabı ─────────────────────────── */

function BankAccountCard({ bank, onEdit }: { bank: PayoutBankAccount; onEdit: () => void }) {
  const t = useT("finance.bank");
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-4 w-4" /> {t("title")}
          </CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          {bank.iban ? t("change") : t("add")}
        </Button>
      </CardHeader>
      <CardContent className="text-sm">
        {bank.iban ? (
          <div className="space-y-0.5">
            <div className="font-mono tabular-nums" dir="ltr">
              {formatIban(bank.iban)}
            </div>
            <div className="text-muted-foreground">{bank.ibanOwnerName}</div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertCircle className="h-4 w-4" /> {t("missing")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const bankSchema = z.object({
  iban: z
    .string()
    .transform((v) => v.replace(/\s+/g, "").toUpperCase())
    .refine((v) => /^TR\d{24}$/.test(v), "validation.ibanInvalid"),
  ibanOwnerName: z.string().trim().min(3, "validation.ibanOwnerRequired"),
});
type BankValues = z.input<typeof bankSchema>;

function BankAccountForm({ bank, onDone }: { bank: PayoutBankAccount; onDone: () => void }) {
  const update = useUpdateBankAccountMutation();
  const t = useT("finance");
  const tCommon = useT("common");
  const form = useForm<BankValues, unknown, z.output<typeof bankSchema>>({
    resolver: zodResolver(bankSchema),
    defaultValues: { iban: formatIban(bank.iban), ibanOwnerName: bank.ibanOwnerName ?? "" },
  });

  async function onSubmit(values: z.output<typeof bankSchema>) {
    try {
      await update.mutateAsync(values);
      toast.success(t("bank.saved"));
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_iban") {
        form.setError("iban", { message: "validation.ibanInvalid" });
        return;
      }
      showApiError(err);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="iban"
          render={({ field }) => (
            <FormItem>
              <FormLabel>IBAN</FormLabel>
              <FormControl>
                <Input
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  autoComplete="off"
                  className="font-mono"
                  dir="ltr"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="ibanOwnerName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("bank.owner")}</FormLabel>
              <FormControl>
                <Input placeholder={t("bank.ownerPlaceholder")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <p className="text-xs text-muted-foreground">{t("bank.changeNotice")}</p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            {t("dismiss")}
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {tCommon("save")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/* ─────────────────────────── Talep ─────────────────────────── */

/** Kullanıcının aktif dildeki ondalık ayırıcıyla yazdığı tutarı sayıya çevirir. */
function parseAmount(raw: string, decimalSeparator: string): number {
  const s = raw.replace(/[^\d.,-]/g, "");
  const normalized =
    decimalSeparator === ","
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(/,/g, "");
  return Number(normalized);
}

function RequestPayoutForm({
  balance,
  bank,
  onDone,
}: {
  balance: PayoutBalance;
  bank: PayoutBankAccount;
  onDone: () => void;
}) {
  const request = useRequestPayoutMutation();
  const t = useT("finance");
  const fmt = useFormat();
  const money = (v: number) => fmt.money(v, balance.currency);
  const parse = (v: string) => parseAmount(v, fmt.decimalSeparator);
  const schema = z.object({
    amount: z.string().refine((v) => {
      const n = parse(v);
      return Number.isFinite(n) && n > 0 && n <= balance.available;
    }, t("request.amountRange", { max: money(balance.available) })),
  });
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount: fmt.number(balance.available, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false,
      }),
    },
  });

  async function onSubmit(values: Values) {
    try {
      await request.mutateAsync({ currency: balance.currency, amount: parse(values.amount) });
      toast.success(t("request.sent"));
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "insufficient_balance") {
        form.setError("amount", { message: t("request.insufficient") });
        return;
      }
      showApiError(err);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("request.amount", { currency: balance.currency })}</FormLabel>
              <FormControl>
                <Input inputMode="decimal" dir="ltr" {...field} />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                {t("request.available", { value: money(balance.available) })}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="text-xs text-muted-foreground">{t("request.targetAccount")}</div>
          <div className="font-mono tabular-nums" dir="ltr">
            {formatIban(bank.iban)}
          </div>
          <div className="text-muted-foreground">{bank.ibanOwnerName}</div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            {t("dismiss")}
          </Button>
          <Button type="submit" disabled={request.isPending}>
            {request.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t("request.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/* ─────────────────────────── Ödemeler ─────────────────────────── */

const STATUS_VARIANT: Record<PayoutStatus, BadgeProps["variant"]> = {
  Requested: "warning",
  Approved: "default",
  Paid: "success",
  Rejected: "destructive",
  Cancelled: "outline",
};

function PayoutsCard({ payouts }: { payouts: Payout[] }) {
  const cancel = useCancelPayoutMutation();
  const t = useT("finance.payouts");
  const tStatus = useT("payoutStatus");
  const fmt = useFormat();

  async function handleCancel(id: string) {
    try {
      await cancel.mutateAsync(id);
      toast.success(t("cancelled"));
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {payouts.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="divide-y">
            {payouts.map((p) => (
              <li key={p.id} className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium tabular-nums">{fmt.money(p.amount, p.currency)}</span>
                    <Badge variant={STATUS_VARIANT[p.status]}>{tStatus(p.status)}</Badge>
                    {p.source === "Admin" ? <Badge variant="outline">{t("byAdmin")}</Badge> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmt.date(p.createdAt, FINANCE_DATE)} · <span dir="ltr">{formatIban(p.iban)}</span>
                  </div>
                  {p.status === "Paid" && p.paidAt ? (
                    <div className="text-xs text-muted-foreground">
                      {t("sentAt", { date: fmt.date(p.paidAt, FINANCE_DATE) })}
                      {p.bankReference ? ` · ${t("receipt", { value: p.bankReference })}` : ""}
                    </div>
                  ) : null}
                  {p.note ? <div className="text-xs">{t("note", { value: p.note })}</div> : null}
                  {p.rejectReason ? (
                    <div className="text-xs text-destructive">
                      {t("rejectReason", { value: p.rejectReason })}
                    </div>
                  ) : null}
                </div>
                {p.status === "Requested" && p.source === "Organizer" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancel.isPending}
                    onClick={() => handleCancel(p.id)}
                  >
                    {t("cancel")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AdjustmentsCard({ adjustments }: { adjustments: BalanceAdjustment[] }) {
  const t = useT("finance.adjustments");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-y">
          {adjustments.map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-4 px-6 py-3 text-sm">
              <div>
                <div>{a.reason}</div>
                <div className="text-xs text-muted-foreground">
                  {fmt.date(a.createdAt, FINANCE_DATE)}
                </div>
              </div>
              <span className={a.amount < 0 ? "tabular-nums text-destructive" : "tabular-nums text-emerald-600"}>
                {a.amount > 0 ? "+" : ""}
                {fmt.money(a.amount, a.currency)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Etkinlikler ─────────────────────────── */

function EventsCard({ events }: { events: PayoutEventEarning[] }) {
  const t = useT("finance.events");
  const fmt = useFormat();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-6 py-3 text-start font-medium">{t("event")}</th>
                <th className="px-4 py-3 text-end font-medium">{t("orders")}</th>
                <th className="px-4 py-3 text-end font-medium">{t("net")}</th>
                <th className="px-6 py-3 text-end font-medium">{t("status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {events.map((e) => (
                <tr key={`${e.eventId}-${e.currency}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium">{e.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("endsAt", { date: fmt.date(e.endsAt, FINANCE_DATE) })}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-end tabular-nums">{fmt.number(e.orderCount)}</td>
                  <td className="px-4 py-3 text-end tabular-nums">{fmt.money(e.net, e.currency)}</td>
                  <td className="px-6 py-3 text-end">
                    {e.settled ? (
                      <Badge variant="success">{t("settled")}</Badge>
                    ) : (
                      <Badge variant="secondary">{t("notEnded")}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
